<script>
    /**
     * SPDX-FileCopyrightText: 2020-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { onMount, setContext, getContext, untrack } from 'svelte';
    // Store and Session
    import { writable } from 'svelte/store';
    import { getInteractionStateStore } from '../../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../../itemsSessionStatusStore.js';
    import itemSessionStatus from '../../itemSessionStatus.js';
    import createChoicesStore from './stores/choices.js';
    import createStateStore from './stores/state.js';
    // Helpers
    import { __, generateElementId, getActualKey, remToPx } from '@oat-sa-private/ui-core';
    import { breakpoints } from '@oat-sa-private/ui-identity';
    import { DraggableList } from '@oat-sa-private/ui-components';
    import FocusHandler from './focus.js';
    // Components
    import Prompt from '../Prompt.svelte';
    import Choice from './Choice.svelte';
    import ItemBlocks from '../../item/blocks/ItemBlocks.svelte';
    import ChoiceFeedbackBlock from '../feedback/ChoiceFeedbackBlock.svelte';
    // Utils
    import preventSpaceScroll from '../util/actions/preventSpaceScroll.js';
    import resizeObserve from '../util/actions/resizeObserve.js';
    import { traceInteraction } from './util.js';
    import shuffleChoiceOptions from '../util/shuffleChoices.js';
    import { getFlowDirection } from '../util/flowDirection.js';
    import {
        areas,
        getAreaStyleOrder,
        getAreasOrder,
        getFocusAreasOrder,
        getPositioning,
        isVerticalPosition,
        positions
    } from '../util/sharedVocabulary.js';
    import {
        clampVerticalChoiceDimension,
        VERTICAL_CHOICE_MAX_BLOCK_SIZE,
        VERTICAL_CHOICE_MIN_BLOCK_SIZE,
        VERTICAL_CHOICE_REMOVER_SIZE
    } from '../util/verticalChoiceSizing.js';

    const qtiClass = 'qti-gapMatchInteraction';

    let {
        // response format
        cardinality = 'multiple',
        baseType = 'directedPair',

        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        // inherited item-level QTI attributes:
        id,
        classes = '',
        language,
        dir,

        // inherited aria attributes:
        role,
        ariaAttrs = {},

        // interaction-level QTI attributes:
        minAssociations = 0,
        maxAssociations = 0, //QTI 2.2 specification contains this attribute, but QTI 2.1 doesn't, so 0 value set for supporting tests based on both the specifications
        prompt,
        choices = [],
        shuffle = false,

        // data attributes
        dataAttrs = {},

        // the parsed item body:
        blockTree = [],
        gaps = []
    } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();
    const writingMode = itemContext && itemContext.getWritingMode && itemContext.getWritingMode();

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let choiceIdentifiers,
        gapIdentifiers,
        maxSelectionMessage,
        minSelectionMessage,
        choicesMaxWidthAttr,
        qtiPosition,
        choiceOptions;

    setDerivedProps();

    function setDerivedProps() {
        choiceIdentifiers = choices.map(choice => choice.identifier);
        gapIdentifiers = gaps.map(gap => gap.identifier);

        maxSelectionMessage = dataAttrs['data-max-selections-message'];
        minSelectionMessage = dataAttrs['data-min-selections-message'];
        choicesMaxWidthAttr = dataAttrs['data-choices-container-width'];

        // presentation information from the shared vocabulary
        qtiPosition = getPositioning(classes);

        choiceOptions = choices;
        if (shuffle) {
            choiceOptions = Array.isArray(choices)
                ? shuffleChoiceOptions(choices, interactionStateStore)
                : shuffleChoiceOptions(Object.keys(choices), interactionStateStore).reduce(
                      (accumulator, key) => ({
                          ...accumulator,
                          [key]: choices[key]
                      }),
                      {}
                  );
        }
    }

    const interactionElementStore = writable(null);
    setContext('interactionElementStore', interactionElementStore);

    const choicesStore = createChoicesStore();
    choicesStore.init(choiceOptions);
    setContext('choicesStore', choicesStore);

    const activeElementStore = writable(null);
    setContext('activeElementStore', activeElementStore);

    let choicesToRender = $state(choiceOptions);

    // area elements
    let choiceArea = $state();
    // unfortunately we don't have way to understand which gap will be rendered first, to set him tabindex=0
    // so this element will handle focusing flow for gaps
    let answerArea = $state();
    let focusRouterElement = $state(); // route focus from answer area to gaps/placeholders
    let interactionElement = $state();

    let itemWidth = $state(remToPx(25)); // choice width in list
    let itemHeight = $state(remToPx(4.25)); // choice height in list
    let itemWidthTop = $state(); //top choices
    let itemHeightTop = $state();
    let itemMaxWidthTop = $state();
    let containerWidthTop = $state();
    let windowWidth = $state(window.innerWidth);
    let keepRowBelowTop = $state(false);
    const choiceMargin = remToPx(2);
    const removeButtonWidth = remToPx(6.25);
    const gapMargin = remToPx(0.5);

    let visualPosition = $derived(windowWidth <= breakpoints.width.large ? positions.top : qtiPosition);
    let flowDir = $derived(getFlowDirection(dir, language, itemContext));
    let areasOrder = $derived(getAreasOrder(visualPosition));
    let focusAreasOrder = $derived(getFocusAreasOrder(visualPosition, flowDir, writingMode));
    let firstAvailableChoiceIndex = $derived(choicesToRender.findIndex(choice => !choice.removed));
    let choiceAreaStyleOrder = $derived(
        getAreaStyleOrder(areas.choices, visualPosition, areasOrder, focusAreasOrder, flowDir, writingMode)
    );
    let answerAreaStyleOrder = $derived(
        getAreaStyleOrder(areas.answers, visualPosition, areasOrder, focusAreasOrder, flowDir, writingMode)
    );
    let isTopChoices = $derived(isVerticalPosition(visualPosition));
    let isVerticalWritingMode = $derived(writingMode === 'vertical-rl');
    // Vertical-rl: content-sized vertical choice glyphs for every position.
    // Placement still follows isTopChoices (column top/bottom vs side stack).
    let useContentSizedChoices = $derived(isVerticalWritingMode);
    let inlineChoiceWidth = $derived.by(() => {
        if (!useContentSizedChoices) {
            return void 0;
        }

        return clampVerticalChoiceDimension(itemWidth);
    });
    let inlineChoiceHeight = $derived.by(() => {
        if (!useContentSizedChoices) {
            return void 0;
        }

        return clampVerticalChoiceDimension(itemHeight);
    });
    let choiceItemWidth = $derived(useContentSizedChoices ? inlineChoiceWidth : itemWidth);
    let choiceItemHeight = $derived(useContentSizedChoices ? inlineChoiceHeight : itemHeight);
    let choiceColumns = $derived(
        isTopChoices && containerWidthTop && itemWidthTop
            ? Math.floor((containerWidthTop + choiceMargin) / (itemWidthTop + choiceMargin))
            : 1
    );

    let focusTracker = null; // handle focus flow
    let isAnswerAreaApplication = $state(false);
    let isInteractionFocused = $state(false); // becomes true when interaction is focused for the first time
    let choiceAreaDroppable = $state(false);

    const answerAreaLabelId = generateElementId('answer-area-label');

    const stateStore = createStateStore();
    setContext('stateStore', stateStore);
    stateStore.init();

    let cssStyle = $derived(
        [
            `--list-width:${
                useContentSizedChoices && inlineChoiceWidth
                    ? `${inlineChoiceWidth}px`
                    : $stateStore.isDragging
                      ? `${$stateStore.gapDimensions.width}px`
                      : 'initial'
            }`,
            `--item-width-top:${
                useContentSizedChoices && isTopChoices && inlineChoiceWidth
                    ? `${inlineChoiceWidth}px`
                    : itemWidthTop
                      ? `${itemWidthTop}px`
                      : 'auto'
            }`,
            `--item-height-top:${
                useContentSizedChoices && isTopChoices && inlineChoiceHeight
                    ? `${inlineChoiceHeight}px`
                    : itemHeightTop
                      ? `${itemHeightTop}px`
                      : 'auto'
            }`,
            `--choice-inline-width:${inlineChoiceWidth ? `${inlineChoiceWidth}px` : 'auto'}`,
            `--choice-inline-height:${inlineChoiceHeight ? `${inlineChoiceHeight}px` : 'auto'}`,
            `--item-width-side:${choiceItemWidth ? `${choiceItemWidth}px` : 'auto'}`,
            `--item-height-side:${choiceItemHeight ? `${choiceItemHeight}px` : 'auto'}`,
            `--vertical-choice-min-block-size:${VERTICAL_CHOICE_MIN_BLOCK_SIZE}px`,
            `--vertical-choice-max-block-size:${VERTICAL_CHOICE_MAX_BLOCK_SIZE}px`,
            `--vertical-choice-remover-size:${VERTICAL_CHOICE_REMOVER_SIZE}px`,
            `--container-width-top: ${containerWidthTop ? `${containerWidthTop}px` : '40rem'}`,
            `--item-max-width-top: ${itemMaxWidthTop ? `${itemMaxWidthTop}` : '33%'}`,
            `--item-max-width-left: ${choicesMaxWidthAttr ? `${choicesMaxWidthAttr}px` : '33%'}`
        ].join(';')
    );

    let disabledBySession = $derived($itemSessionStatusStore === itemSessionStatus.closed);

    $effect(() => {
        stateStore.setDisabledState(disabledBySession);
    });
    stateStore.setSelected(loadResponse());
    $effect(() => {
        if ($interactionStateStore) {
            untrack(() => {
                stateStore.setSelected(loadResponse()); //sync 'stateStore' by values from '$interactionStateStore'
            });
        }
    });

    // list of choices which already moved to gaps
    let selectedSnapshot = $derived($stateStore.selected); //also updated directly, not only with derived
    // snapshot of selectedChoices before last change

    // number on choices in list which can be matched
    let choicesCount = $derived(choicesToRender.filter(choice => !choice.removed).length);
    let availableGaps = $derived($stateStore.gapsNumber - $stateStore.selectedGaps.length);

    $effect(() => {
        if ($stateStore) {
            untrack(() => {
                choicesToRender = getChoiceToRender(choicesToRender);
            });
        }
    });

    $effect(() => {
        if (!isAnswerAreaApplication && $activeElementStore) {
            isAnswerAreaApplication = true;
        }
    });
    $effect(() => {
        let width;
        let height;

        if (useContentSizedChoices) {
            width = inlineChoiceWidth;
            height = inlineChoiceHeight;
        } else if (isTopChoices) {
            width = Math.max(itemWidth, itemWidthTop || 0);
            height = Math.max(itemHeight, itemHeightTop || 0);
        } else {
            width = itemWidth;
            height = itemHeight;
        }

        stateStore.setGapDimensions(width, height);
    });
    $effect(() => {
        stateStore.setVerticalInlineGaps(isVerticalWritingMode);
    });

    const {
        draggableGroupKey,
        ariaDescribedByContainerIdForUnselectedOptions,
        ariaDescribedByContainerIdForEmptyGap,
        ariaDescribedByContainerIdForMatchedGap
    } = $stateStore;

    const shadowDraggableGroupKey = generateElementId('shadow');
    const ariaLiveContainerId = generateElementId('ariaDescribedBy');

    focusTracker = new FocusHandler();
    setContext('focusTracker', focusTracker); //allow using in any children component

    /**
     * Handle drop to gap
     */
    setContext('handleDrop', function (pairs) {
        const dropAreaKeys = pairs.map(({ dropAreaKey }) => dropAreaKey);
        const newValue = filterResponseByGap(dropAreaKeys);
        pairs.forEach(({ draggableKey, dropAreaKey }) => {
            // if draggableKey is null it means we dont want to add new element to response. Other words - remove pair for 'dropAreaKey'
            if (draggableKey) {
                newValue.push([draggableKey, dropAreaKey]);
            }
        });
        saveResponse(newValue);
        // if all gaps filled - focus answer area
        const filledGaps = loadResponse();
        if (filledGaps.length === $stateStore.gapsNumber) {
            focusTracker.focusAnswerArea();
        }
    });

    onMount(() => {
        // do initial response definition
        // do it here, because 'required' state of gaps can be used only after gap rendering
        if (!interactionStateStore.hasResponse()) {
            interactionStateStore.merge({ qtiClass });
            const response = {
                cardinality,
                baseType,
                value: null
            };
            interactionStateStore.setResponseValue(response, getValidity([]));
        }
        focusTracker.initAreas(choiceArea, answerArea, focusRouterElement);
        interactionElementStore.set(interactionElement);
    });

    /**
     * Check if choice ids and gap ids in response pairs are all valid ids
     * so that response can be discarded if it was corrupted somehow
     * @param {string[][]} responsePairs - formatted for multiple cardinality
     * @returns {Boolean}
     */
    function validateResponsePairs(responsePairs) {
        return responsePairs?.every(pair => choiceIdentifiers.includes(pair[0]) && gapIdentifiers.includes(pair[1]));
    }

    /**
     * Load the interaction response
     * @returns {array}
     */
    function loadResponse() {
        let storedResponse = interactionStateStore.getResponseValue();

        if (typeof storedResponse === 'undefined' || storedResponse === null) {
            storedResponse = [];
        }
        if (cardinality === 'single') {
            storedResponse = [storedResponse];
        }
        if (!validateResponsePairs(storedResponse)) {
            storedResponse = [];
        }

        return storedResponse;
    }

    /**
     * Format and store selected value in interactionStateStore
     * @param {array} value - array of gap and matched choice
     */
    function saveResponse(value) {
        if (!validateResponsePairs(value)) {
            console.error('invalid response detected:', value);
            return;
        }
        const validity = getValidity(value);

        if (value.length === 0) {
            value = null;
        } else {
            value = cardinality === 'single' ? value[0] : value;
        }
        const response = {
            cardinality,
            baseType,
            value
        };
        interactionStateStore.setResponseValue(response, validity);
    }

    /**
     * Get the validity of the current state
     * @param {array} matches - gap and matched choice
     * @returns {Boolean}
     */
    function getValidity(matches) {
        const matchesCount = matches.length;
        let validity = true;

        // validate minAssociations and maxAssociations properties of choices
        if (
            (minAssociations !== 0 && matchesCount < minAssociations) ||
            (maxAssociations !== 0 && matchesCount > maxAssociations)
        ) {
            validity = false;
        }

        if (validity) {
            //pre saved updating selectedChoices only for validation goals. After successful save we also have to call this method
            stateStore.setSelected(matches);
            // validate matchMin and matchMax properties of choices
            validity = Object.entries($choicesStore).every(([choiceId, choice]) => {
                const choiceUsageCount = $stateStore.selectedChoices ? $stateStore.selectedChoices[choiceId] || 0 : 0;
                if (
                    (choice.matchMin !== 0 && choiceUsageCount < choice.matchMin) ||
                    (choice.matchMax !== 0 && choiceUsageCount > choice.matchMax)
                ) {
                    return false;
                }
                return true;
            });
        }
        // check that each required gap has pair in 'matchedGaps'
        if (validity) {
            const matchedGaps = matches.map(pair => pair[1]);
            validity = Object.entries($stateStore.gapRequiredAttribute).every(
                ([gap, required]) => (required && matchedGaps.includes(gap)) || !required
            );
        }

        return validity;
    }

    /**
     * Update choices list and set/remove 'removed' flag
     * @param {array} choicesList - full list of choices
     * @returns {array} - list of modified choices prepared for rendering
     */
    function getChoiceToRender(choicesList) {
        const choicesWithRemovedState = [];
        const choicesWithoutRemovedState = [];
        choicesList.forEach(choice => {
            // choice dropped elsewhere and doesn't belong the list anymore, so we do not render choice, but empty space
            if (
                $stateStore.selectedChoices[choice.identifier] &&
                !(parseInt(choice.matchMax, 10) - $stateStore.selectedChoices[choice.identifier])
            ) {
                choice.removed = true;
                choicesWithRemovedState.push(choice);
                return;
            }
            //choice a part of the list, so we render it
            if (choice.removed) {
                choice.removed = false;
            }
            choicesWithoutRemovedState.push(choice);
        });

        keepRowBelowTop =
            isTopChoices &&
            !useContentSizedChoices &&
            choicesWithRemovedState.length > 0 &&
            choicesWithoutRemovedState.length % choiceColumns === 0;

        return [...choicesWithoutRemovedState, ...choicesWithRemovedState];
    }

    /**
     * Return current response without pairs which contains pointed gaps
     * @param {array} gapIds - array of gap identifier ids
     * @returns {array}
     */
    function filterResponseByGap(gapIds) {
        const response = loadResponse();
        // cardinality 'single'
        if (typeof response[0] === 'string') {
            // always empty
            return [];
        }
        return response.filter(pair => !gapIds.includes(pair[1]));
    }

    /**
     * Handle drop in list
     * @param {CustomEvent} e
     */
    function handleDropToList(e) {
        selectedSnapshot = $stateStore.selected;
        const { dropAreaKey } = $activeElementStore;
        const newValue = filterResponseByGap([dropAreaKey]);
        saveResponse(newValue);
        activeElementStore.set(null);
        choiceAreaDroppable = false;
        dispatchTraceInteraction(e);
    }

    /**
     * Focus first element into answer area
     */
    function handleFocus() {
        // focus gap possible only if choice activated
        if (!focusTracker.focusChoiceInFirstGap() && $activeElementStore) {
            focusTracker.focusFirstEmptyGap();
        }
        focusTracker.makeAnswerAreaUnfocusable();
        isAnswerAreaApplication = true;
    }

    /**
     * Handle focus on answer area
     */
    function handleFocusAnswerArea() {
        // if answer area is focused by any way, than focusRouterElement must be focusable
        focusTracker.makeAnswerAreaFocusable();
        isAnswerAreaApplication = false;
    }

    /**
     * Focus previous/next gap
     * @param {CustomEvent} e
     * @returns {Boolean|void}
     */
    function handleKeyDown(e) {
        const keys = {
            left: 'left',
            up: 'up',
            right: 'right',
            down: 'down',
            tab: 'tab'
        };
        const pressedKey = getActualKey(e);

        if (pressedKey === keys.tab) {
            activeElementStore.set(null);
            if (
                !e.shiftKey &&
                focusAreasOrder[0] === areas.answers &&
                e.target === focusRouterElement
            ) {
                e.preventDefault();
                focusTracker.focusChoiceListArea();
                return false;
            }
            return true;
        }

        if (!keys[pressedKey]) {
            return false;
        }
        const focusableElements = $activeElementStore
            ? focusTracker.getAllFocusableGaps()
            : focusTracker.getAllFocusableChoicesInGaps();
        const handler = focusTracker.moveFocus(focusableElements);

        if (!handler) {
            return false;
        }
        const isRTL = window.getComputedStyle(e.target).getPropertyValue('direction') === 'rtl';

        switch (pressedKey) {
            case keys.left:
                isRTL ? handler.focusNext() : handler.focusPrevious();
                e.stopPropagation();
                break;
            case keys.up:
                handler.focusPrevious();
                e.stopPropagation();
                break;
            case keys.right:
                isRTL ? handler.focusPrevious() : handler.focusNext();
                e.stopPropagation();
                break;
            case keys.down:
                handler.focusNext();
                e.stopPropagation();
                break;
        }
        dispatchTraceInteraction(e);
    }

    /**
     * Clear activated choice if 'esc' button was pressed
     * @param {KeyboardEvent} e
     */
    const resetActivatedState = function (e) {
        const pressedKey = getActualKey(e);
        if (pressedKey === 'esc' && activeElementStore) {
            const { draggableKey } = $activeElementStore;
            // calculate right message for announcing
            let content = '';
            choiceOptions.some(choice => {
                if (choice.identifier === draggableKey) {
                    content = choice.content;
                    return true;
                }
                return false;
            });

            activeElementStore.set(null);
            stateStore.setConfirmation(__('%s has not been placed.', content));
            focusTracker.focusChoiceListArea();
        }
    };

    /**
     * Clear activated choice if mouse click was out of list and out of gap
     */
    function clearActivatedChoice() {
        activeElementStore.set(null);
        //if we loosing 'tab focus' by mouse click, so we always should restore focusability of answerArea
        focusTracker.makeAnswerAreaFocusable();
    }

    /**
     * Flag that focus jump inside interaction
     */
    function handleInteractionFocusIn() {
        if (!isInteractionFocused) {
            isInteractionFocused = true;
        }
    }

    /**
     * Add 'droppable' styles
     */
    function handleDragOver() {
        choiceAreaDroppable = true;
    }

    /**
     * Remove 'droppable' styles
     */
    function handleDragOut() {
        choiceAreaDroppable = false;
    }

    /**
     * Measure interaction container's width
     * @param {CustomEvent} event
     */
    function handleInteractionResized(event) {
        const { width: interactionWidth } = event.detail;
        if (interactionWidth) {
            if (choicesMaxWidthAttr) {
                containerWidthTop = Math.min(interactionWidth, choicesMaxWidthAttr);
            } else {
                containerWidthTop = interactionWidth;
            }

            if (containerWidthTop < breakpoints.width.medium) {
                if (choicesMaxWidthAttr && interactionWidth > choicesMaxWidthAttr + removeButtonWidth + gapMargin) {
                    itemMaxWidthTop = '100%';
                } else {
                    //gaps/choices in answer-area shouldn't get wider than interaction
                    itemMaxWidthTop = `calc(100% - ${removeButtonWidth + gapMargin}px)`;
                }
            } else if (containerWidthTop < breakpoints.width.large) {
                itemMaxWidthTop = `calc(50% - ${choiceMargin + 1}px)`;
            } else {
                itemMaxWidthTop = `calc(33% - ${choiceMargin + 1}px)`;
            }
        }
    }

    /**
     * Dispatches a trace event
     * @param {CustomEvent} e
     */
    function dispatchTraceInteraction(e) {
        const eventData = traceInteraction(e, $stateStore.selected, selectedSnapshot);
        const interactionEvent = new CustomEvent('interactiontrace', eventData);
        interactionElement.dispatchEvent(interactionEvent);
    }
</script>

<style>
    .qti-gapMatchInteraction {
        --remover-width: 6rem;
        --gap-between: 4rem;
        --gap-outer: 1rem;
        --gap-inner: 2.5rem;
        --fontsize-gapmatch: calc(var(--fontsize-body) * 1.25);
        font-size: var(--fontsize-gapmatch);

        &.layout-horizontal .answer-area {
            writing-mode: vertical-rl;
            line-height: normal;
        }

        &.layout-horizontal.choices-left .choices-area {
            width: var(--item-width-side);
            min-width: var(--item-width-side);
            max-width: var(--item-max-width-left);
            flex: 0 0 auto;
        }

        &.layout-horizontal.choices-top {
            --remover-width: var(--vertical-choice-remover-size);
            --vertical-choice-stack-gap: calc(var(--remover-width) + 1rem);

            & .qti-flow-container {
                writing-mode: horizontal-tb;
                flex-flow: column nowrap;
                align-items: flex-start;
            }

            & .answer-area {
                writing-mode: vertical-rl;
                line-height: normal;
                width: 100%;
                align-self: stretch;
            }

            & .choices-area {
                width: var(--choice-inline-width);
                min-width: var(--choice-inline-width);
                max-width: none;
                flex: 0 0 auto;
                align-self: flex-start;

                & :global(.height-keeper) {
                    width: var(--choice-inline-width);
                    min-width: var(--choice-inline-width);
                    max-width: var(--choice-inline-width);
                }

                & :global(.height-keeper.vertical) {
                    --grid-gap: var(--vertical-choice-stack-gap);
                }

                & :global(.height-keeper.vertical .draggable-list) {
                    grid-auto-rows: auto;
                }

                & :global(.item-btn) {
                    writing-mode: vertical-rl;
                }

                & :global(.item-btn-container:not(.removable) .item-btn) {
                    width: var(--choice-inline-width);
                    height: var(--choice-inline-height);
                    max-height: var(--vertical-choice-max-block-size);
                    overflow: hidden;
                }

                & :global(.item-btn-container:not(.removable) .item-btn .icon-container) {
                    padding-inline-start: 0;
                }

                /* vertical-rl top uses a stacked bank; do not apply LTR top removed-row rules */
                & :global(li.removed) {
                    display: none;
                }
            }

            & .position-top .choices-area,
            & .position-bottom .choices-area {
                margin-inline: 0;
            }
        }
    }
    .answer-area {
        position: relative;
        color: var(--color-text-default);
        outline: none;
        /* multiplier makes minimal dynamic line-height (1.5) match 5.5rem height of gaps */
        line-height: calc(var(--line-height-default) * 3.67rem);

        &:focus-visible {
            @add-mixin outline-focus-after;
            &::after {
                border-radius: var(--radius-large);
                pointer-events: none;
            }
        }
    }

    .shadow-choices-container {
        width: 1px;
        height: 1px;
        overflow: hidden;
        position: absolute;
    }

    .qti-block {
        display: flex;

        & :global(.icon) {
            height: var(--fontsize-gapmatch);
        }
        &.qti-flow-container {
            & :global(.item-btn-container .item-btn) {
                min-height: initial;
                width: auto;
            }
            & :global(.item-btn-container .item-btn .label-container) {
                padding-bottom: 0.25rem;
                padding-top: 0.25rem;
                line-height: 1.2;

                & > :global(.blocks) {
                    align-items: center;
                }
                & > :global(.blocks.complex) {
                    padding: 0.5rem 0;
                }

                & :global(img) {
                    max-width: 10rem;
                    max-height: 10rem;
                    margin: 0 var(--space-1x);
                }
            }
        }
        &[aria-controls] {
            cursor: auto;
        }
    }

    .choices-left {
        & .qti-flow-container {
            direction: ltr;
        }

        &.layout-horizontal .qti-flow-container {
            --remover-width: var(--vertical-choice-remover-size);
            --vertical-choice-stack-gap: calc(var(--remover-width) + 1rem);
            writing-mode: horizontal-tb;

            & :global(.item-btn) {
                writing-mode: vertical-rl;
            }
        }

        & .choices-area {
            max-width: var(--item-max-width-left);
            flex: 0 0 auto;
            width: var(--list-width);

            & :global(.height-keeper) {
                width: var(--list-width);
            }
        }

        &.layout-horizontal .choices-area {
            align-self: flex-start;

            & :global(.height-keeper) {
                width: var(--item-width-side);
            }

            & :global(.height-keeper.vertical) {
                --grid-gap: var(--vertical-choice-stack-gap);
            }

            & :global(.height-keeper.vertical .draggable-list) {
                grid-auto-rows: auto;
            }

            & :global(.item-btn-container:not(.removable) .item-btn) {
                width: var(--item-width-side);
                height: var(--item-height-side);
                max-height: var(--vertical-choice-max-block-size);
                overflow: hidden;
            }

            & :global(.item-btn-container.removable) {
                display: flex;
                flex-direction: column;
                width: var(--item-width-side);
                height: calc(var(--item-height-side) + var(--remover-width));
                max-height: calc(var(--vertical-choice-max-block-size) + var(--remover-width));
                overflow: hidden;
            }

            & :global(.item-btn-container.removable .item-btn) {
                padding-inline: 0;
                padding-block: 0;
                flex: 1 1 auto;
                min-height: 0;
                width: 100%;
                height: auto;
                overflow: hidden;
                border-radius: var(--radius-large) var(--radius-large) 0 0;
                border-bottom-width: 0;
            }

            & :global(.item-btn-container.removable .remover) {
                position: relative;
                top: auto;
                bottom: auto;
                inset-inline: auto;
                flex: 0 0 var(--remover-width);
                width: 100%;
                height: var(--remover-width);
                align-self: stretch;
                border-radius: 0 0 var(--radius-large) var(--radius-large);
                border-top-width: 0;
            }

            & :global(.item-btn-container.removable.placed .remover) {
                border-color: var(--color-border-selected);
                border-top-width: 0;
            }
        }

        & .shadow-choices-side {
            width: auto;
        }
        & .position-left .choices-area {
            margin-inline-end: var(--gap-between);
        }
        & .position-right .choices-area {
            margin-inline-start: var(--gap-between);
        }
    }

    .choices-top {
        & .qti-block {
            flex-flow: column nowrap;
        }

        & .shadow-choices-area {
            width: var(--container-width-top);
            display: flex; /* size choices by content if it's < 33% */

            & :global(.height-keeper) {
                max-width: var(--item-max-width-top);
            }
        }

        & .qti-block .choices-area {
            max-width: var(--container-width-top);
            width: 100%;
            align-self: flex-start;

            & :global(.targetable .draggable-list) {
                padding-bottom: 0;
            }
            & :global(.item-btn-container .item-btn) {
                width: var(--item-width-top);
                height: var(--item-height-top);
            }
            & :global(.height-keeper .draggable-list li) {
                margin-bottom: 1.5rem;
                margin-top: 0.5rem;
            }
            &.keep-row-below-top :global(li.removed ~ li.removed) {
                display: none;
            }
            &:not(.keep-row-below-top) :global(li.removed) {
                display: none;
            }
        }

        & .position-top .choices-area {
            margin-top: var(--gap-outer);
            margin-bottom: var(--gap-inner);
        }
        & .position-bottom .choices-area {
            margin-top: var(--gap-inner);
            margin-bottom: var(--gap-outer);
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} onclick={clearActivatedChoice} onkeydown={resetActivatedState} />

<div
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    class:choices-top={isTopChoices}
    class:choices-left={!isTopChoices}
    class:layout-horizontal={isVerticalWritingMode}
    onfocusin={handleInteractionFocusIn}
    use:preventSpaceScroll
    bind:this={interactionElement}
    aria-disabled={disabledBySession}
    style={cssStyle}
    lang={language}
    {id}
    {dir}
    {role}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt}
        <Prompt blockTree={prompt} />
    {/if}

    <ChoiceFeedbackBlock
        maxChoices={maxAssociations}
        minChoices={minAssociations}
        type="associations"
        qtiMaxChoicesMessage={maxSelectionMessage}
        qtiMinChoicesMessage={minSelectionMessage}
        {isInteractionFocused}
        {interactionElement}
        selectedNumber={$stateStore.selected.length}
        lang={instructionsLang} />

    <div aria-live="assertive" class="hidden" id={ariaLiveContainerId} lang={instructionsLang}>
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        {@html $stateStore.confirmation}
    </div>
    <div id={ariaDescribedByContainerIdForUnselectedOptions} class="hidden" lang={instructionsLang}>
        {__('Press enter or space to grab and browse the answer area.')}
        {#if choicesCount > 1}{__('To move to next available option, use the arrow keys.')}{/if}
    </div>
    <div id={ariaDescribedByContainerIdForEmptyGap} class="hidden" lang={instructionsLang}>
        {__('Press enter or space to place down.')}
        {#if availableGaps > 1}{__('To browse other gaps use the arrow keys.')}{/if}
        {__('Press escape to cancel.')}
    </div>
    <div id={ariaDescribedByContainerIdForMatchedGap} class="hidden" lang={instructionsLang}>
        {__('Press enter or space to grab.')}
        {#if $stateStore.selected.length > 1}{__('To move to next available answer, use the arrow keys.')}{/if}
        {__('Double press shift+tab to return to choices.')}
    </div>
    <div
        class="qti-flow-container qti-block position-{visualPosition}"
        class:layout-horizontal={useContentSizedChoices && !isTopChoices}
        aria-controls={ariaLiveContainerId}
        use:resizeObserve={handleInteractionResized}>
        {#if isTopChoices || useContentSizedChoices}
            <div class="shadow-choices-container" aria-hidden="true" inert>
                <div class="shadow-choices-area" class:shadow-choices-side={useContentSizedChoices}>
                    {#if useContentSizedChoices}
                        <DraggableList
                            disabled={true}
                            isListVertical={true}
                            bind:itemWidth
                            bind:itemHeight
                            {instructionsLang}>
                            {#each choiceOptions as choice (choice.identifier)}
                                <Choice
                                    isInChoiceArea={true}
                                    key={choice.identifier}
                                    disabled={true}
                                    {shadowDraggableGroupKey} />
                            {/each}
                        </DraggableList>
                    {:else}
                        <DraggableList
                            disabled={true}
                            bind:itemWidth={itemWidthTop}
                            bind:itemHeight={itemHeightTop}
                            {instructionsLang}>
                            {#each choiceOptions as choice (choice.identifier)}
                                <Choice
                                    isInChoiceArea={true}
                                    key={choice.identifier}
                                    disabled={true}
                                    {shadowDraggableGroupKey} />
                            {/each}
                        </DraggableList>
                    {/if}
                </div>
            </div>
        {/if}
        {#each focusAreasOrder as area (area)}
            {#if area === areas.choices}
                <div
                    class="choices-area"
                    class:keep-row-below-top={keepRowBelowTop}
                    style:direction={!isTopChoices ? flowDir : undefined}
                    style:order={choiceAreaStyleOrder}
                    bind:this={choiceArea}>
                    {#if useContentSizedChoices}
                        <DraggableList
                            {draggableGroupKey}
                            dropAreaKey="choices"
                            disabled={disabledBySession}
                            targetable={$activeElementStore && $stateStore.isDragging}
                            targeted={choiceAreaDroppable}
                            isListVertical={true}
                            ariaLabel={__('Available options')}
                            presentItemsCount={choicesCount}
                            {instructionsLang}
                            on:dragOver={handleDragOver}
                            on:dragOut={handleDragOut}
                            on:drop={!disabledBySession && handleDropToList}>
                            {#each choicesToRender as choice, i (choice.identifier)}
                                <Choice
                                    isInChoiceArea={true}
                                    key={choice.identifier}
                                    order={i + 1}
                                    tabbable={i === firstAvailableChoiceIndex}
                                    disabled={disabledBySession}
                                    removed={choice.removed}
                                    choiceWidth={inlineChoiceWidth}
                                    choiceHeight={inlineChoiceHeight} />
                            {/each}
                        </DraggableList>
                    {:else}
                        <DraggableList
                            {draggableGroupKey}
                            dropAreaKey="choices"
                            disabled={disabledBySession}
                            targetable={$activeElementStore && $stateStore.isDragging}
                            targeted={choiceAreaDroppable}
                            isListVertical={!isTopChoices}
                            mode={isTopChoices ? 'flex' : void 0}
                            bind:itemWidth
                            bind:itemHeight
                            ariaLabel={__('Available options')}
                            presentItemsCount={choicesCount}
                            {instructionsLang}
                            on:dragOver={handleDragOver}
                            on:dragOut={handleDragOut}
                            on:drop={!disabledBySession && handleDropToList}>
                            {#each choicesToRender as choice, i (choice.identifier)}
                                <Choice
                                    isInChoiceArea={true}
                                    key={choice.identifier}
                                    order={i + 1}
                                    tabbable={i === firstAvailableChoiceIndex}
                                    disabled={disabledBySession}
                                    removed={choice.removed} />
                            {/each}
                        </DraggableList>
                    {/if}
                </div>
            {:else if area === areas.answers}
                <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
                <div
                    class="answer-area"
                    style:direction={!isTopChoices ? flowDir : undefined}
                    style:order={answerAreaStyleOrder}
                    tabindex="0"
                    bind:this={answerArea}
                    role={isAnswerAreaApplication ? 'application' : 'article'}
                    aria-labelledby={!isAnswerAreaApplication ? answerAreaLabelId : void 0}
                    onfocus={!disabledBySession && handleFocusAnswerArea}
                    onkeydown={!disabledBySession && handleKeyDown}>
                    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
                    <div
                        bind:this={focusRouterElement}
                        class="visually-hidden"
                        tabindex="0"
                        onfocus={!disabledBySession && handleFocus}>
                    </div>
                    <ItemBlocks {blockTree} />
                    {#if isAnswerAreaApplication}
                        <span id={answerAreaLabelId} class="hidden" lang={instructionsLang}>
                            {__('Text Passage. To read use the arrow keys.')}
                        </span>
                    {/if}
                </div>
            {/if}
        {/each}
    </div>
</div>
